import React from 'react';
import { SwitchToggle } from 'components/Form/SwitchToggle';
import FormGroup from 'components/Form/FormGroup';
import { ButtonToggle } from 'containers/Programmations/ProgV2/Preview/Sidebar/ButtonToggle';
import { SelectPrintView } from 'containers/Programmations/ProgV2/Preview/Sidebar/SelectPrintView';
import { MIN_NB_OF_WEEKS_TO_GROUP_WEEKS } from 'containers/Programmations/ProgV2/constants';
import { useTranslation } from 'react-i18next';
import { useUpdateProgrammation } from 'containers/Programmations/atoms/useUpdateProgrammation';
import {
  useHideTitle,
  useProg,
  useProgView,
} from 'containers/Programmations/atoms/progAtoms';
import { PrintConfig } from 'containers/Programmations/ProgV2/Preview/types';
import { PrintView } from 'containers/Programmations/ProgV2/Print/types';
import { TFunction } from 'i18next';
import { useNbOfMatieres } from 'containers/Programmations/atoms/matieresAtoms';

interface Props {
  matiereId?: string;
  onUpdatePrintConfig: (v: PrintConfig) => void;
  printConfig: PrintConfig;
}

function getPreventCuttingOptionLabel(
  view: PrintView,
  isProgression: boolean,
  t: TFunction<['common', 'programmation']>,
) {
  switch (view) {
    case 'periode_x_domaine':
      return t('programmation:preventCuttingADomain');
    case 'domaine_x_periode':
      if (isProgression) {
        return t('programmation:preventCuttingAWeek');
      }
      return t('programmation:preventCuttingAPeriod');
    case 'periode_x_matiere':
      return t('programmation:preventCuttingAMatiere');
    case 'matiere_x_periode':
      return t('programmation:preventCuttingAPeriod');
    case 'periode_x_semaine':
      return t('programmation:preventCuttingAWeek');
  }
}

function getBreakBeforeOptionLabel(
  view: PrintView,
  isProgression: boolean,
  t: TFunction<['common', 'programmation']>,
) {
  switch (view) {
    case 'periode_x_domaine':
      return t('programmation:breakBeforeADomain');
    case 'domaine_x_periode':
      if (isProgression) {
        return t('programmation:breakBeforeAWeek');
      }
      return t('programmation:breakBeforeAPeriod');
    case 'periode_x_matiere':
      return t('programmation:breakBeforeAMatiere');
    case 'matiere_x_periode':
      if (isProgression) {
        return t('programmation:breakBeforeAWeek');
      }
      return t('programmation:breakBeforeAPeriod');
    case 'periode_x_semaine':
      return t('programmation:breakBeforeAWeek');
  }
}

export function ElementsAndDisposition(props: Props) {
  const { matiereId, onUpdatePrintConfig, printConfig } = props;
  const { t } = useTranslation(['common', 'programmation']);
  const updateProg = useUpdateProgrammation();
  const hideTitle = useHideTitle();
  const progView = useProgView();
  const prog = useProg();
  const progMatieres = prog?.matieres || [];
  const isCompatiblePeriodeSemaine = progView === 'week';
  const nbOfMatieres = useNbOfMatieres() || 0;
  const isMatiereEmpty = nbOfMatieres === 0;
  return (
    <>
      <SwitchToggle
        checked={hideTitle}
        onChange={() => updateProg({ hideTitle: !hideTitle })}
        label={t('programmation:hideTitle')}
      />
      {isMatiereEmpty ? null : (
        <>
          {progMatieres.length > 1 && (
            <div>
              <FormGroup label={t('programmation:displayedMatieres')}>
                <ButtonToggle
                  fields={progMatieres.map(m => ({
                    value: m.id,
                    label: m.name,
                  }))}
                  values={printConfig.matiereIds}
                  onChange={values =>
                    onUpdatePrintConfig({
                      ...printConfig,
                      matiereIds: values,
                    })
                  }
                />
              </FormGroup>
            </div>
          )}
          <div>
            <SelectPrintView
              printConfig={printConfig}
              onUpdatePrintConfig={onUpdatePrintConfig}
              mode={matiereId ? 'classic' : 'unified'}
              isProgression={progView === 'week'}
            />
          </div>

          {isCompatiblePeriodeSemaine &&
            (prog?.periodes || []).length > MIN_NB_OF_WEEKS_TO_GROUP_WEEKS &&
            (printConfig.view === 'periode_x_domaine' ||
              printConfig.view === 'domaine_x_periode') && (
              <SwitchToggle
                checked={printConfig.groupWeeksByPeriodes}
                onChange={value =>
                  onUpdatePrintConfig({
                    ...printConfig,
                    groupWeeksByPeriodes: value,
                  })
                }
                label={t('programmation:groupWeeksByPeriodes')}
              />
            )}
          <SwitchToggle
            checked={printConfig.breakBefore}
            onChange={value =>
              onUpdatePrintConfig({
                ...printConfig,
                breakBefore: value,
              })
            }
            label={getBreakBeforeOptionLabel(
              printConfig.view,
              progView === 'week',
              t,
            )}
          />
          <SwitchToggle
            checked={printConfig.preventCutting && !printConfig.breakBefore}
            onChange={value =>
              onUpdatePrintConfig({
                ...printConfig,
                preventCutting: value,
              })
            }
            label={getPreventCuttingOptionLabel(
              printConfig.view,
              progView === 'week',
              t,
            )}
            disabled={printConfig.breakBefore}
          />
          {printConfig.view !== 'periode_x_semaine' && (
            <SwitchToggle
              checked={printConfig.rowTitleOrientation === 'horizontal'}
              onChange={value =>
                onUpdatePrintConfig({
                  ...printConfig,
                  rowTitleOrientation: value ? 'horizontal' : 'vertical',
                })
              }
              label={t('programmation:rowTitleHorizontal')}
            />
          )}
        </>
      )}
    </>
  );
}
